import { createSlice } from "@reduxjs/toolkit";
import { sortAllView } from "../utils";

const salesRepDefaultColumns = ["FULL NAME", "CREATED BY", "CREATE DATE", "REP STATUS"];
const quoteDefaultColumns = [
  "QUOTE NUMBER",
  "CUSTOMER",
  "EXPIRY DATE",
  "SALES REP",
  "PRICE LIST",
  "REQUESTED SHIP DATE",
  "CURRENCY",
  "CUSTOMER REFERENCE",
  "BILLING ADDRESS",
  "SHIPPING ADDRESS",
  "STATUS",
  "SUB TOTAL",
  "TAX",
  "TOTAL AMOUNT",
];

const salesOrderDefaultColumns = [
  "SALES NO",
  "ORDER DATE",
  "CUSTOMER",
  "FACILITY",
  "PRICELIST",
  "SALESPERSON",
  "REQUESTED SHIP DATE",
  "CURRENCY",
  "CUSTOMER REFERENCE",
  "BILLING ADDRESS",
  "SHIPPING ADDRESS",
  "STATUS",
  "SUB TOTAL",
  "TAX",
  "TOTAL",
];

const customerReturnDefaultColumns = [
  "RETURN NO",
  "CUSTOMER",
  "RETURN TYPE",
  "STATUS",
  "SALES NO",
  "INVOICE NO",
  "CREATE DATE",
  "RETURN DATE",
  "SALESPERSON",
  "CURRENCY",
  "TO FACILITY",
  "CUSTOMER REFERENCE",
  "SUB TOTAL",
  "TAX",
  "TOTAL",
];

const initialState = {
  salesReps: { count: 0, results: [] },
  salesRepsParams: { limit: 10, offset: 0 },
  salesRepLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: salesRepDefaultColumns,
      allViews: { "Default View": salesRepDefaultColumns },
      nameOfViews: [],
    },
  },
  quotation: {},
  quotations: { count: 0, results: [] },
  quoteParams: { limit: 10, offset: 0 },
  quotationSelection: [],
  quoteLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: quoteDefaultColumns,
      allViews: { "Default View": quoteDefaultColumns },
      nameOfViews: [],
    },
  },
  salesOrder: {},
  salesOrders: { count: 0, results: [] },
  salesOrderParams: { limit: 10, offset: 0 },
  salesOrderSelection: [],
  salesOrderLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: salesOrderDefaultColumns,
      allViews: { "Default View": salesOrderDefaultColumns },
      nameOfViews: [],
    },
  },
  customerReturn: {},
  customerReturns: { count: 0, results: [] },
  customerReturnParams: { limit: 10, offset: 0 },
  customerReturnSelection: [],
  customerReturnLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: customerReturnDefaultColumns,
      allViews: { "Default View": customerReturnDefaultColumns },
      nameOfViews: [],
    },
  },
};

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    getSalesReps: (state, action) => {
      state.salesReps = action.payload;
    },
    setSalesRepsParams: (state, action) => {
      state.salesRepsParams = action.payload;
    },
    updateSalesRepsParams: (state, action) => {
      state.salesRepsParams = { ...state.salesRepsParams, ...action.payload };
    },
    setSalesRepLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.salesRepLayout = {
        ...state.salesRepLayout,
        listView: {
          ...state.salesRepLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.salesRepLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    getQuotation: (state, action) => {
      state.quotation = action.payload;
    },
    clearQuotation: state => {
      state.quotation = {};
    },
    setQuotations: (state, action) => {
      state.quotations = action.payload;
    },
    setQuotationSelection: (state, action) => {
      state.quotationSelection = action.payload;
    },
    setQuoteParams: (state, action) => {
      state.quoteParams = action.payload;
    },
    updateQuoteParams: (state, action) => {
      state.quoteParams = { ...state.quoteParams, ...action.payload };
    },
    resetQuoteParams: state => {
      state.quoteParams = { limit: state.quoteParams.limit, offset: 0 };
    },
    setQuoteLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.quoteLayout = {
        ...state.quoteLayout,
        listView: {
          ...state.quoteLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.quoteLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    getSalesOrder: (state, action) => {
      state.salesOrder = action.payload;
    },
    clearSalesOrder: state => {
      state.salesOrder = {};
    },
    setSalesOrders: (state, action) => {
      state.salesOrders = action.payload;
    },
    setSalesOrderSelection: (state, action) => {
      state.salesOrderSelection = action.payload;
    },
    setSalesOrderParams: (state, action) => {
      state.salesOrderParams = action.payload;
    },
    updateSalesOrderParams: (state, action) => {
      state.salesOrderParams = { ...state.salesOrderParams, ...action.payload };
    },
    resetSalesOrderParams: state => {
      state.salesOrderParams = { limit: state.salesOrderParams.limit, offset: 0 };
    },
    setSalesOrderLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.salesOrderLayout = {
        ...state.salesOrderLayout,
        listView: {
          ...state.salesOrderLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.salesOrderLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    getCustomerReturn: (state, action) => {
      state.customerReturn = action.payload;
    },
    clearCustomerReturn: state => {
      state.customerReturn = {};
    },
    setCustomerReturns: (state, action) => {
      state.customerReturns = action.payload;
    },
    setCustomerReturnSelection: (state, action) => {
      state.customerReturnSelection = action.payload;
    },
    setCustomerReturnParams: (state, action) => {
      state.customerReturnParams = action.payload;
    },
    updateCustomerReturnParams: (state, action) => {
      state.customerReturnParams = { ...state.customerReturnParams, ...action.payload };
    },
    resetCustomerReturnParams: state => {
      state.customerReturnParams = { limit: state.customerReturnParams.limit, offset: 0 };
    },
    setCustomerReturnLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.customerReturnLayout = {
        ...state.customerReturnLayout,
        listView: {
          ...state.customerReturnLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.customerReturnLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
  },
});

export const {
  getSalesReps,
  setSalesRepsParams,
  updateSalesRepsParams,
  setSalesRepLayoutAllViews,
  setQuotations,
  getQuotation,
  clearQuotation,
  setQuoteParams,
  updateQuoteParams,
  resetQuoteParams,
  setQuoteLayoutAllViews,
  setQuotationSelection,
  setSalesOrders,
  getSalesOrder,
  clearSalesOrder,
  setSalesOrderParams,
  updateSalesOrderParams,
  resetSalesOrderParams,
  setSalesOrderLayoutAllViews,
  setSalesOrderSelection,
  getCustomerReturn,
  clearCustomerReturn,
  setCustomerReturns,
  setCustomerReturnSelection,
  setCustomerReturnParams,
  updateCustomerReturnParams,
  resetCustomerReturnParams,
  setCustomerReturnLayoutAllViews,
} = salesSlice.actions;

export default salesSlice.reducer;
